import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Chip,
  IconButton,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useAgent from '../../hooks/useAgent';
import { useAuth } from '../../context/authContext';
import { toast } from 'react-toastify';
import { formatDate } from '../../util/formatHelper';
import ChatConversation from '../chat/ChatConversation';

export default function EscalatedChatsView() {
  const {
    fetchEscalatedSessions,
    loadSessionMessages,
    sendHumanMessage,
    fetchEscalatedCounts,
    claimEscalatedSessionLock,
    releaseEscalatedSessionLock,
  } = useAgent();
  const { refreshCountForMenuItem } = useAuth();
  const refreshCountRef = useRef(refreshCountForMenuItem);
  const loadSessionMessagesRef = useRef(loadSessionMessages);
  const claimLockRef = useRef(claimEscalatedSessionLock);
  const releaseLockRef = useRef(releaseEscalatedSessionLock);
  const selectedSessionTokenRef = useRef(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionToken, setSelectedSessionToken] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ total: 0, awaitingHuman: 0, awaitingCustomer: 0 });
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    refreshCountRef.current = refreshCountForMenuItem;
  }, [refreshCountForMenuItem]);

  const loadEscalatedSessions = useCallback(async () => {
    try {
      const [sessionResponse, countResponse] = await Promise.all([
        fetchEscalatedSessions(),
        fetchEscalatedCounts().catch(() => null),
      ]);
      const sortedSessions = [...(sessionResponse.sessions || [])].sort((a, b) => {
        if (a.awaitingHumanResponse === b.awaitingHumanResponse) {
          return new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);
        }
        return a.awaitingHumanResponse ? -1 : 1;
      });
      setSessions(sortedSessions);
      if (countResponse) {
        setCounts(countResponse);
      }
    } catch (e) {
      console.error(e);
      // silent fail for polling
    }
  }, [fetchEscalatedSessions, fetchEscalatedCounts]);

  useEffect(() => {
    loadEscalatedSessions();
    const interval = setInterval(loadEscalatedSessions, 30000);
    return () => clearInterval(interval);
  }, [loadEscalatedSessions]);

  useEffect(() => {
    refreshCountRef.current?.('Eskalierte Chats');
  }, []);

  useEffect(() => {
    loadSessionMessagesRef.current = loadSessionMessages;
  }, [loadSessionMessages]);

  useEffect(() => {
    claimLockRef.current = claimEscalatedSessionLock;
  }, [claimEscalatedSessionLock]);

  useEffect(() => {
    releaseLockRef.current = releaseEscalatedSessionLock;
  }, [releaseEscalatedSessionLock]);

  useEffect(() => {
    selectedSessionTokenRef.current = selectedSessionToken;
  }, [selectedSessionToken]);

  useEffect(() => {
    return () => {
      const token = selectedSessionTokenRef.current;
      if (token) {
        releaseLockRef.current?.(token).catch(() => {});
      }
    };
  }, []);

  const updateSessionLock = useCallback((token, lockState) => {
    setSessions(prev =>
      prev.map(session =>
        session.sessionToken === token
          ? {
              ...session,
              locked: !!lockState?.locked,
              lockedByOther: !!lockState?.lockedByOther,
              editingLock: lockState?.editingLock || null,
            }
          : session
      )
    );
  }, []);

  const loadSession = useCallback(async (token, { claimLock = true } = {}) => {
    try {
      setLoading(true);
      if (claimLock) {
        const lockState = await claimLockRef.current(token);
        setSelectedLock(lockState);
        updateSessionLock(token, lockState);
      }
      const data = await loadSessionMessagesRef.current(token);
      setMessages(data.messages || []);
      if (!claimLock) {
        setSelectedLock(data);
        updateSessionLock(token, data);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error('Fehler beim Laden der Nachrichten');
    }
  }, [updateSessionLock]);

  useEffect(() => {
    if (selectedSessionToken) {
      loadSession(selectedSessionToken);
    }
  }, [selectedSessionToken, loadSession]);

  const handleSend = async () => {
    if (!input.trim() || !selectedSessionToken || loading || selectedLock?.lockedByOther) return;
    try {
      await sendHumanMessage(selectedSessionToken, input.trim());
      setInput('');
      await loadSession(selectedSessionToken, { claimLock: false });
      await loadEscalatedSessions();
      refreshCountRef.current?.('Eskalierte Chats');
      inputRef.current?.focus();
    } catch (e) {
      if (e?.code === 'AGENT_SESSION_LOCKED') {
        toast.error('Dieser Chat wird bereits von einem anderen Mitarbeiter bearbeitet');
      } else {
        toast.error('Fehler beim Senden');
      }
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const selectedSession = sessions.find(s => s.sessionToken === selectedSessionToken);
  const lockInfo = selectedLock?.editingLock || selectedSession?.editingLock || null;
  const isLockedByOther = !!(selectedLock?.lockedByOther || selectedSession?.lockedByOther);
  const filteredMessages = messages
    .filter(m => m.role !== 'system' && m.role !== 'tool')
    .map(message =>
      message.role === 'assistant'
        ? { ...message, senderName: message.senderName || 'AI-Assistent' }
        : message
    );
  const inputDisabled = !selectedSessionToken || loading || isLockedByOther;
  const canSend = Boolean(input.trim()) && !!selectedSessionToken && !loading && !isLockedByOther;
  const showSidebar = !isMobile || !selectedSessionToken;
  const showChat = !isMobile || !!selectedSessionToken;
  const handleSelectSession = token => {
    if (!token || token === selectedSessionToken) return;
    const previousToken = selectedSessionToken;
    setInput('');
    setMessages([]);
    setSelectedLock(null);
    setSelectedSessionToken(token);
    if (previousToken) {
      releaseLockRef.current?.(previousToken).catch(() => {});
    }
  };
  const handleBackToSessions = () => {
    if (isMobile) {
      if (selectedSessionToken) {
        releaseLockRef.current?.(selectedSessionToken).catch(() => {});
      }
      setSelectedSessionToken(null);
      setSelectedLock(null);
    }
  };

  useEffect(() => {
    if (!selectedSessionToken || !selectedLock?.locked) return undefined;
    const interval = setInterval(async () => {
      try {
        const lockState = await claimLockRef.current(selectedSessionToken);
        setSelectedLock(lockState);
        updateSessionLock(selectedSessionToken, lockState);
      } catch (_) {
        // Polling errors should not interrupt the current operator view.
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [selectedSessionToken, selectedLock?.locked, updateSessionLock]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        height: isMobile ? 'auto' : 'calc(100vh - 100px)',
        gap: 2,
        p: 2,
      }}
    >
      {showSidebar && (
        <Paper
          sx={{
            width: isMobile ? '100%' : 320,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          <Box
            p={2}
            borderBottom={1}
            borderColor='divider'
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Typography variant='h6' fontWeight={600}>
              Eskalierte Chats
            </Typography>
            <Chip
              label={`${counts.awaitingHuman || 0} offen`}
              color={counts.awaitingHuman ? 'error' : 'default'}
              size='small'
            />
          </Box>
          <List sx={{ flex: 1, overflowY: 'auto' }}>
            {sessions.map(session => {
              const lockedByOther = !!session.lockedByOther;
              const lockName = session.editingLock?.userName || 'einem anderen Mitarbeiter';
              const secondaryText = lockedByOther
                ? `Wird von ${lockName} bearbeitet`
                : session.escalationReason;
              return (
                <ListItemButton
                  key={session.sessionToken}
                  selected={session.sessionToken === selectedSessionToken}
                  onClick={() => handleSelectSession(session.sessionToken)}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    gap: 0.5,
                    opacity: lockedByOther ? 0.55 : 1,
                  }}
                >
                  <Box display='flex' justifyContent='space-between' width='100%' gap={1}>
                    <Typography variant='caption' color='text.secondary'>
                      {formatDate(session.lastMessageAt)}
                    </Typography>
                    <Chip
                      label={
                        lockedByOther
                          ? 'In Bearbeitung'
                          : session.awaitingHumanResponse
                            ? 'Offen'
                            : 'Beantwortet'
                      }
                      color={
                        lockedByOther ? 'default' : session.awaitingHumanResponse ? 'error' : 'success'
                      }
                      size='small'
                      sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }}
                    />
                  </Box>
                  <ListItemText
                    primary={session.preview || 'Keine Vorschau'}
                    primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                    secondary={secondaryText}
                    secondaryTypographyProps={{
                      noWrap: true,
                      variant: 'caption',
                      color: lockedByOther ? 'warning.main' : 'text.secondary',
                    }}
                    sx={{ m: 0, width: '100%' }}
                  />
                </ListItemButton>
              );
            })}
            {sessions.length === 0 && (
              <Box p={3} textAlign='center'>
                <Typography variant='body2' color='text.secondary'>
                  Keine offenen Fälle.
                </Typography>
              </Box>
            )}
          </List>
        </Paper>
      )}

      {showChat && (
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            minHeight: isMobile ? 400 : 0,
          }}
        >
          {selectedSessionToken ? (
            <>
              <Box
                p={2}
                borderBottom={1}
                borderColor='divider'
                sx={{
                  bgcolor: 'background.default',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                {isMobile && (
                  <IconButton size='small' onClick={handleBackToSessions}>
                    <ArrowBackIcon fontSize='small' />
                  </IconButton>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='subtitle1' fontWeight={600}>
                    Konversation
                  </Typography>
                  {selectedSession?.escalationReason && (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {selectedSession.escalationReason}
                    </Typography>
                  )}
                </Box>
              </Box>
              {loading ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    p: 2,
                    bgcolor: theme =>
                      theme.palette.mode === 'light' ? 'grey.50' : 'rgba(0,0,0,0.2)',
                  }}
                >
                  {isLockedByOther && (
                    <Alert severity='warning' sx={{ mb: 2 }}>
                      Dieser Chat wird bereits von {lockInfo?.userName || 'einem anderen Mitarbeiter'}{' '}
                      bearbeitet. Du kannst die Unterhaltung ansehen, aber nicht antworten.
                    </Alert>
                  )}
                  <ChatConversation
                    messages={filteredMessages}
                    inputValue={input}
                    onInputChange={e => setInput(e.target.value)}
                    onSend={handleSend}
                    onKeyDown={handleKeyDown}
                    inputRef={inputRef}
                    inputDisabled={inputDisabled}
                    canSend={canSend}
                    placeholder='Antwort schreiben...'
                    isTyping={false}
                    sessionEnded={false}
                    localRole='assistant'
                    showLocalSenderNames
                    contentSx={isLockedByOther ? { opacity: 0.45 } : null}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              flex={1}
              flexDirection='column'
              gap={2}
            >
              <Typography color='text.secondary'>
                Wähle einen Fall aus der Liste, um ihn zu bearbeiten.
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
